import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, linkedSignal, OnDestroy, OnInit, output, signal } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { UsersDetailsEndpoints } from '@pims-endpoints';
import { ServiceUpgradeInjectorService } from '@shims';
import { PhotosConstants, Users, Visit, VisitDetailPhotos } from '@types-lib';
import { SnackNotificationsDialogService } from '@ui-lib/services';
import { ENVIRONMENT, PIMSEnvironment } from 'environments';
import { catchError, EMPTY, from, map, Observable, of, Subscription } from 'rxjs';
import { Order } from '../../../../../../../../../../angularJs/src/app/prototypes/order.prototype';
import Auth from '../../../../../../../../../../angularJs/src/app/services/auth.service';
import { Constants } from '../../../../../../../../../../angularJs/src/app/services/constants.service';
import Media from '../../../../../../../../../../angularJs/src/app/services/media.service';
import { TASKS } from '../../../../../../../../../../angularJs/src/app/services/order.service';
import Photos from '../../../../../../../../../../angularJs/src/app/services/photos.service';
import { ROLES } from '../../../../../../../../../../angularJs/src/app/services/user.service';
import { FileDirective } from '../../../../../../../../directives/file.directive';
import { GalleryDialogComponent } from '../../components/gallery/gallery-dialog.component';
import { DeletePhotoDialogComponent } from './delete-photo-dialog/delete-photo-dialog.component';
import { rejectionNoteValidatorFactory } from '@shared-utils';

@Component({
  selector: 'task-photos',
  imports: [
    // TODO: Angular 19 WARNINGS: Unused import. Delete if unnecessary
    // NgOptimizedImage,
    // RotateImageDirective
    //________________
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FileDirective,
  ],
  templateUrl: './task-photos.component.html',
  styleUrl: './task-photos.component.scss',
})
export class TaskPhotosComponent implements OnInit, OnDestroy {
  private Media = inject(Media);
  private Constants = inject(Constants);
  private Auth = inject(Auth);
  private Photos = inject(Photos);
  private ServiceUpgradeInjectorService = inject(ServiceUpgradeInjectorService);
  private dialog = inject(MatDialog);

  readonly snackNotificationService = inject(SnackNotificationsDialogService);
  private env = inject<PIMSEnvironment>(ENVIRONMENT);
  readonly photos = input<VisitDetailPhotos.Photo[] | undefined>();
  private readonly usersDetailsEndpoint = new UsersDetailsEndpoints();
  visit = input<Visit.Visit>();
  diagnosis = input<string | null>('');
  task = input<VisitDetailPhotos.Task>();
  types = input<VisitDetailPhotos.IdTypes[] | null>([]);
  errorMessage = input<string | null>(null);
  isUpdatingPhoto = input<boolean>();
  rejected = output<VisitDetailPhotos.PropsPayload>();
  approved = output<VisitDetailPhotos.PropsPayload>();
  deleted = output<null>();
  reloaded = output<null>();
  // Image rotation values
  originalSizes: { [index: number]: { width: number; height: number } } = {};
  rotations: { [index: number]: number } = {};

  // Constants for Photos ID and Statuses
  readonly PHOTO_ID_ID = PhotosConstants.PhotoTypes.Id.id;
  readonly ADDITIONAL_ID_ID = PhotosConstants.PhotoTypes.AdditionalId.id;
  readonly PHOTO_PENDING_REVIEW = PhotosConstants.PhotoStatuses.PendingReview;
  readonly PHOTO_APPROVED = PhotosConstants.PhotoStatuses.Approved;
  readonly PHOTO_REJECTED = PhotosConstants.PhotoStatuses.Rejected;

  subscription = new Subscription();
  localErrorMessage = linkedSignal(() => this.errorMessage());
  photoFormGroups = computed<Map<number, FormGroup>>(
    () =>
      new Map(
        this.photos()?.map(photo => {
          const patientFirstName = this.visit()?.user?.firstname;
          const rejectionNoteDefault =
             !photo.file.rejection_note && this.visit()?.user?.firstname
              ? `Hello ${patientFirstName}, \n\n`
              : photo.file.rejection_note;
          const isActive = () => {
            const current = this.taskPhotos()?.find(p => p.id === photo.id);
            return !!current?.rejected;
          };
          return [
            photo.id,
            new FormGroup({
              photoType: new FormControl<string | null>({ value: photo.file.id_type, disabled: !this.isPending() }, [
                Validators.required,
              ]),
              rejectionNote: new FormControl<string>({ value: rejectionNoteDefault, disabled: !this.isPending() }, [
                rejectionNoteValidatorFactory(patientFirstName, isActive, 3),
              ]),
            }),
          ];
        }),
      ),
  );
  // changes to FormControls inside an array or map aren't tracked aren't directly tracked in computed signals
  // so we need to manually notify computed signals of a form change manually - Probably resolved in Angular 20 with SignalForms
  formChangesTrigger = signal(0);

  taskPhotos = linkedSignal<VisitDetailPhotos.Photo[] | undefined>(() =>
    this.photos()?.map(photo => ({
      ...photo,
      canBeDeleted: this.canPhotoBeDeleted(photo),
      class: this.photoIdClass(photo),
    })),
  );
  allPhotoIdTypesSelected = computed(() => {
    this.formChangesTrigger();
    return this.taskPhotos()?.every(
      photo => !this.isPhotoId(photo) || this.photoFormGroups().get(photo.id)?.get('photoType')?.valid,
    );
  });
  disableCTA = computed(() => {
    if (this.taskPhotos()?.length && this.types()?.length) {
      const idPhoto = this.taskPhotos()?.find(photo => [this.PHOTO_ID_ID, this.ADDITIONAL_ID_ID].includes(photo.id));
      if (idPhoto) {
        this.formChangesTrigger();
        const photoType = this.photoFormGroups().get(idPhoto.id)?.value.photoType;
        return !!(idPhoto.file.id_type || [null, 'null'].includes(photoType));
      }
      return false;
    }
    return false;
  });
  approveCTADisabled = computed(
    () =>
      this.hasRejected() ||
      this.hasDeleted() ||
      this.isOrderLocked() ||
      !this.allPhotoIdTypesSelected() ||
      this.disableCTA() ||
      this.isUpdatingPhoto(),
  );
  rejectCTADisabled = computed(
    () => !this.hasRejected() || this.isOrderLocked() || this.hasEmptyRejectionNote() || this.isUpdatingPhoto(),
  );
  hasEmptyRejectionNote = computed<boolean>(() => {
    this.formChangesTrigger();
    return !!this.taskPhotos()?.some(
      (photo: VisitDetailPhotos.Photo) =>
        photo.rejected && this.photoFormGroups().get(photo.id)?.get('rejectionNote')?.invalid,
    );
  });
  hasRejected = computed(() => !!this.taskPhotos()?.some(photo => photo.rejected));
  hasDeleted = computed(() => !!this.taskPhotos()?.some(photo => photo.deleted));
  isUserDeleted = computed(() => this.visit()?.user?.status === 2);
  waitingForUpload = computed<boolean | undefined>(() =>
    this.taskPhotos()?.some((photo: VisitDetailPhotos.Photo) => photo.file?.status === this.PHOTO_REJECTED),
  );

  photoApproved = computed(() => {
    const approved = this.taskPhotos()?.every(
      (photo: VisitDetailPhotos.Photo) => photo.file?.status === this.PHOTO_APPROVED,
    );
    const photos = this.taskPhotos();
    const approverId = photos?.length && photos[0].file.reviewed_by;
    const approvedDate = photos?.length && photos[0].file.reviewed;
    let idReviewer: Observable<Users.User | undefined> = of(undefined);
    if (approved && approverId) {
      idReviewer = this.usersDetailsEndpoint.get(approverId).pipe(map(data => data));
    }
    return {
      approved,
      idReviewer,
      approvedDate,
    };
  });
  needsDiagnosis = false;
  labels: any = [];
  order: Order | null = null;
  isOrderLocked = signal<boolean | undefined>(this.order?.isLocked());
  isPending = computed<boolean | undefined>(() =>
    this.taskPhotos()?.every((photo: VisitDetailPhotos.Photo) => photo.file?.status === this.PHOTO_PENDING_REVIEW),
  );
  rotation = 0;

  //not needed???
  labels$: Observable<any[]> = from([]);

  ngOnInit() {
    this.initialise();
  }

  initialise() {
    if (this.visit()?.needsDiagnosis && (this.visit()?.needsDiagnosis as () => void)()) {
      this.needsDiagnosis = true;
      this.labels$ = this.fetchLabels().pipe(map(data => data));
      this.subscription.add(
        this.fetchLabels().subscribe((data: any) => {
          this.labels = data;
        }),
      );
    } else {
      this.needsDiagnosis = false;
    }
  }

  photoIdClass(photo: VisitDetailPhotos.Photo) {
    const lowerCased = photo.text && photo.text.toLowerCase();
    const regex = /photo\s*id/;
    return regex.test(lowerCased) ? 'id-photo' : '';
  }

  reload() {
    this.reloaded.emit(null);
  }

  fetchLabels() {
    return this.Media.getLabelsNew(this.visit()).pipe(
      catchError((error: any) => {
        console.log(error);
        return EMPTY;
      }),
    );
  }

  setRejectStatus(id: number, status: boolean) {
    this.taskPhotos.update(current => current?.map(p => (p.id === id ? { ...p, rejected: status } : p)));
    const control = this.photoFormGroups().get(id)?.get('rejectionNote');
    control?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    this.onFormControlChange(this.photoFormGroups().get(id) as FormGroup);
  }

  onFormControlChange(formGroup?: FormGroup) {
    formGroup?.updateValueAndValidity();
    this.formChangesTrigger.update(n => (n + 1) % 2);
  }

  approve() {
    const payload = {
      photos: (this.taskPhotos() as VisitDetailPhotos.Photo[]).map((taskPhoto: VisitDetailPhotos.Photo) => {
        return {
          ...taskPhoto,
          file: {
            ...taskPhoto.file,
            id_type: this.photoFormGroups().get(taskPhoto.id)?.get('photoType')?.value,
            rejection_note: this.photoFormGroups().get(taskPhoto.id)?.get('rejectionNote')?.value,
          },
        };
      }),
    };
    this.approved.emit({ payload });
  }

  reject() {
    if (this.hasEmptyRejectionNote()) {
      this.updateErrorMessage('Please provide a rejection message');
      return;
    }
    const payload = {
      photos: (this.taskPhotos() as VisitDetailPhotos.Photo[]).map((taskPhoto: VisitDetailPhotos.Photo) => {
        return {
          ...taskPhoto,
          file: {
            ...taskPhoto.file,
            id_type: this.photoFormGroups().get(taskPhoto.id)?.get('photoType')?.value,
            rejection_note: this.photoFormGroups().get(taskPhoto.id)?.get('rejectionNote')?.value,
          },
        };
      }),
    };
    this.rejected.emit({ payload });
  }

  isIdPhotoTask() {
    return this.task()?.type === TASKS.ID_PHOTO;
  }

  requiresAction() {
    return this.isPending() && !this.photoApproved().approved && !this.waitingForUpload();
  }

  isPhotoId(file: VisitDetailPhotos.Photo) {
    return !!this.types() && [this.ADDITIONAL_ID_ID, this.PHOTO_ID_ID].includes(file.id);
  }

  deletePhoto(file: VisitDetailPhotos.Photo) {
    this.subscription.add(
      from(this.Photos.deletePhotoNew(file.file.id))
        .pipe(
          catchError(err => {
            this.snackNotificationService.openErrorMessage('Error while deleting photo');
            return of(err);
          }),
        )
        .subscribe(() => {
          this.taskPhotos.update(currentPhotos =>
            currentPhotos?.map(photo => (photo.id === file.id ? { ...photo, deleted: true } : photo)),
          );
          this.deleted.emit(null);
          this.ServiceUpgradeInjectorService.getAngularJsRootScope().$emit('visit:photo-deleted', file);
        }),
    );
  }

  canPhotoBeDeleted(file: VisitDetailPhotos.Photo) {
    const isAllowedToDelete = [ROLES.SUPER.NAME].some(allowedRole =>
      Object.values(this.Auth.profile.roles).includes(allowedRole),
    );
    return this.isPhotoId(file) && !file.deleted && isAllowedToDelete;
  }

  openPhotoGallery(index = 0): void {
    const dialogRef = this.dialog.open(GalleryDialogComponent, {
      data: {
        files: this.taskPhotos(),
        index: index,
        requiresAction: this.requiresAction.bind(this),
      },
    });

    this.subscription.add(
      dialogRef
        .afterClosed()
        .subscribe((photoGalleryPhotos: VisitDetailPhotos.Photo[]) =>
          this.taskPhotos.update(_currentPhotos => [...photoGalleryPhotos]),
        ),
    );
  }

  showDeletePhotoConfirmationDialog(file: VisitDetailPhotos.Photo) {
    const dialogRef = this.dialog.open(DeletePhotoDialogComponent, {
      data: { file },
    });
    this.subscription.add(
      dialogRef.afterClosed().subscribe(result => {
        if (result && result.approve) {
          this.deletePhoto(file);
        }
      }),
    );
  }

  rotate(photoID: number) {
    this.rotations[photoID] = this.rotations[photoID] ? (this.rotations[photoID] += 90) : 90;
    const isVertical = (this.rotations[photoID] / 90) % 2 === 1;
    const gallery = $(`#photo-image-container${photoID}`),
      image = $(`#photo-responsive${photoID}`),
      imageWidth = image.width(),
      galleryHeight = gallery.height();
    if (!this.originalSizes[photoID]) {
      this.originalSizes[photoID] = {
        height: galleryHeight,
        width: imageWidth,
      };
    }

    const maxWidth = this.originalSizes[photoID].width;
    const newGalleryHeight = isVertical ? this.originalSizes[photoID].width : this.originalSizes[photoID].height;
    gallery.css({
      height: `${newGalleryHeight}px`,
    });
    image.css({
      width: `${maxWidth}px`,
      transform: `rotate(${this.rotations[photoID]}deg)`,
    });
  }

  updateErrorMessage(errorMsg: string | null) {
    this.localErrorMessage.update(() => errorMsg);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
